import React, { useState } from 'react';
import { CheckCircle2, ArrowUpRight } from 'lucide-react';
import { RevealSection } from '../Animations';

const ContactForm = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const formData = new FormData(e.target as HTMLFormElement);

    try {
      const response = await fetch('https://formspree.io/emergeautomations@gmail.com', {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      });

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        const errorData = await response.json();
        if (errorData.code === 'FORM_NOT_FOUND' || response.status === 404) {
          setError(
            'Formspree setup required: Please check your email (emergeautomations@gmail.com) for a confirmation link from Formspree to activate this form.'
          );
        } else {
          setError(errorData.error || 'Submission failed. Please try again.');
        }
      }
    } catch {
      setError('An error occurred. Please check your connection or try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const fieldClass =
    'w-full px-4 py-3.5 rounded-[var(--radius-md)] bg-[color:var(--color-bg-base)] border border-white/10 text-white placeholder:text-zinc-600 text-[15px] focus:outline-none focus:border-[color:var(--color-accent)] transition-colors';

  return (
    <section id="contact" className="section-padding relative">
      <div className="container-custom">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <RevealSection className="lg:col-span-5">
            <span className="eyebrow">07. Contact</span>
            <div className="mt-3 mb-8 h-px bg-white/10" />
            <h2 className="text-[clamp(2rem,4.5vw,3.5rem)] font-display font-semibold text-white leading-[1.05] tracking-[-0.03em] mb-6">
              Let's grow your business
            </h2>
            <p className="text-[17px] text-zinc-400 leading-[1.65] mb-10 max-w-lg">
              Ready to save time and scale? We help businesses in the UK and USA implement AI without the stress.
            </p>

            <div className="grid sm:grid-cols-2 gap-8 pt-8 border-t border-white/10">
              <div>
                <p className="eyebrow mb-3">Email</p>
                <a
                  href="mailto:emergeautomations@gmail.com"
                  className="text-[15px] font-medium text-white hover:text-[color:var(--color-accent)] transition-colors break-all"
                >
                  emergeautomations@gmail.com
                </a>
              </div>
              <div>
                <p className="eyebrow mb-3">Social</p>
                <a
                  href="https://linkedin.com/company/emergeautomations"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[15px] font-medium text-white hover:text-[color:var(--color-accent)] transition-colors"
                >
                  LinkedIn
                  <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
                </a>
              </div>
            </div>
          </RevealSection>

          <div className="lg:col-span-7">
            <div className="p-6 md:p-10 rounded-[var(--radius-xl)] bg-[color:var(--color-surface-1)] border border-white/10">
              {error && (
                <div className="mb-6 p-4 rounded-[var(--radius-sm)] border border-red-500/30 bg-red-500/[0.08] text-red-300 text-sm">
                  {error}
                </div>
              )}
              {!isSubmitted ? (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label htmlFor="name" className="eyebrow mb-2 block">
                      Full name
                    </label>
                    <input
                      required
                      type="text"
                      name="name"
                      id="name"
                      autoComplete="name"
                      placeholder="Alex Chen"
                      className={fieldClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="eyebrow mb-2 block">
                      Work email
                    </label>
                    <input
                      required
                      type="email"
                      name="email"
                      id="email"
                      autoComplete="email"
                      placeholder="alex@company.com"
                      className={fieldClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="eyebrow mb-2 block">
                      How can we help?
                    </label>
                    <textarea
                      required
                      rows={5}
                      name="message"
                      id="message"
                      autoComplete="off"
                      placeholder="Tell us about your business and what you'd like to automate."
                      className={`${fieldClass} resize-none`}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary w-full justify-center"
                  >
                    {isLoading ? (
                      <span className="w-5 h-5 border-2 border-[color:var(--color-accent-on)] border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        Send message
                        <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
                      </>
                    )}
                  </button>
                </form>
              ) : (
                <div className="py-12 text-center">
                  <div className="w-14 h-14 rounded-full border border-[color:var(--color-accent)]/30 bg-[color:var(--color-accent)]/[0.06] flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-6 h-6 text-[color:var(--color-accent)]" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">Message received</h3>
                  <p className="text-zinc-400 mb-6">Our team will contact you within 24 hours.</p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="text-sm font-medium text-[color:var(--color-accent)] hover:underline"
                  >
                    Send another message
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
